// Injected side-effect ports (docs/10 §2.4). player-core is DOM-based but touches
// NO storage, service workers, network, or `window.location` on its own. Every
// impure service the engine needs is supplied through one of these interfaces, so
// the SAME playback code runs two ways:
//   - player (kiosk): a Cache-API-backed MediaSource (offline) + a batching logger
//   - cms (preview):  a plain-URL MediaSource (live fetch) + a no-op/console logger

export type LogLevel = 'info' | 'warn' | 'error';

/**
 * Resolves a manifest media URL to a playable `src` (blob:, data:, http(s):) or
 * null on a miss. The engine builds `<img>/<video>/<iframe>` from whatever this
 * returns — it never fetches or caches itself. type=url items are passed through
 * here too; a plain source returns them as-is (rendered live in the iframe).
 */
export interface MediaSource {
  resolveSrc(url: string): Promise<string | null>;
}

/** Structured event sink. The player batches+ships these; the CMS drops them. */
export interface PlayerLogger {
  add(level: LogLevel, code: string, message?: string, itemId?: string): void;
}

/** Discards every event — the default for previews that don't ship telemetry. */
export class NoopLogger implements PlayerLogger {
  add(): void {
    /* no-op */
  }
}

/**
 * The CMS media strategy: resolve a manifest URL to a URL the browser fetches
 * directly (no Cache API). By default the URL is used verbatim; pass a rewriter
 * to prefix relative `/v1/player/media/:id` paths with an absolute API origin.
 */
export class PlainUrlMediaSource implements MediaSource {
  constructor(private readonly rewrite: (url: string) => string = (u) => u) {}
  async resolveSrc(url: string): Promise<string | null> {
    return this.rewrite(url);
  }
}
